import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "motion/react";
import { PageShell, PageHero } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";
import { ArrowRight, ArrowUpRight, Lightbulb, Eye, Cpu, MapPin, GraduationCap, BookOpen, Newspaper, Video, Handshake } from "lucide-react";
import { AuthorCard } from "@/components/AuthorCard";
import portraitThinking from "@/assets/portrait-thinking.webp";
import { ogUrl } from "@/lib/ogSections";
import { ASK_ARIF_ARTICLES, getCategory } from "@/lib/askArif";
import { hreflangLinks } from "@/lib/hreflang";
import { pageSchemaScript } from "@/seo/schema";


const THINKING = [
  { Icon: Lightbulb, title: "Project Learnings", body: "Patterns extracted from kilometres of perimeter and hundreds of integrated rooms." },
  { Icon: Eye, title: "Industry Observations", body: "What's shifting across security, automation and luxury technology — and why." },
  { Icon: Cpu, title: "Technology Trends", body: "Where edge AI, sensor fusion and unified control fabric are actually heading." },
  { Icon: MapPin, title: "Field Experiences", body: "Notes from sites, commissioning days and owner briefings across India." },
  { Icon: GraduationCap, title: "Lessons From Execution", body: "Decisions that compounded — and the small ones that didn't survive deployment." },
];

const SECTIONS = [
  { to: "/insights/learning-centre", Icon: BookOpen, title: "Learning Centre", desc: "Foundational concepts across security, automation, AV, cinema and intelligent buildings." },
  { to: "/insights/articles-guides", Icon: Newspaper, title: "Articles & Guides", desc: "Practical guides on CCTV, access control, fire alarm, perimeter, home automation and AV." },
  { to: "/blog", Icon: Newspaper, title: "Blog", desc: "Field notes on perimeter security, electric fencing and AV integration." },
  { to: "/insights/media", Icon: Video, title: "Media", desc: "Videos, talks, seminar clips and educational insights from the field." },
  { to: "/insights/partners", Icon: Handshake, title: "Partners", desc: "OEM and integration partner ecosystem — referenced selectively per project." }, 
] as const;

const FEATURED_SLUGS = [
  "how-does-perimeter-security-work",
  "edge-ai-in-surveillance",
  "what-is-an-intelligent-building",
  "common-mistakes-in-home-theatre-projects",
  "how-much-does-home-automation-cost",
  "ai-search-and-intelligent-buildings",
] as const;

const ARTICLES = FEATURED_SLUGS
  .map((slug) => {
    const a = ASK_ARIF_ARTICLES.find((x) => x.slug === slug);
    if (!a) return null;
    return {
      slug: a.slug,
      category: getCategory(a.category)?.title ?? "Insights",
      title: a.question,
      excerpt: a.shortAnswer,
    };
  })
  .filter((x): x is { slug: string; category: string; title: string; excerpt: string } => !!x);

const CATEGORIES = ["Security Trends", "Home Automation", "Luxury Home Technology", "Smart Cities", "AI Surveillance", "Perimeter Security", "Home Theatre", "Auditorium Design", "Future Technology", "Technology Leadership"];

const INSIGHTS_TITLE = "Insights — Articles, Guides & Trends | Arif Khan";
const INSIGHTS_DESC =
  "Future-ready articles on security infrastructure, smart automation, luxury living technology, and the next decade of building technology.";

export const Route = createFileRoute("/insights/")({
  component: InsightsPage,
  head: () => ({
    meta: [
      { title: INSIGHTS_TITLE },
      { name: "description", content: INSIGHTS_DESC },
      { property: "og:title", content: "Insights | Arif Khan" },
      { property: "og:description", content: "Future-ready articles on security, automation and luxury living technology." },
      { property: "og:url", content: "https://arifkhanglobal.com/insights" },
      { property: "og:image", content: ogUrl(portraitThinking) },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: ogUrl(portraitThinking) },
    ],
    links: [
      { rel: "canonical", href: "https://arifkhanglobal.com/insights" },
      ...hreflangLinks("/insights"),
      { rel: "alternate", type: "text/markdown", href: "https://arifkhanglobal.com/insights.md", title: "Markdown mirror" },
    ],
    scripts: [
      pageSchemaScript(
        {
          path: "/insights",
          type: "CollectionPage",
          name: INSIGHTS_TITLE,
          description: INSIGHTS_DESC,
          breadcrumb: [{ name: "Insights", url: "/insights" }],
        },
        [
          {
            "@type": "ItemList",
            "@id": "https://arifkhanglobal.com/insights#list",
            name: "Insights — Articles & Guides",
            itemListOrder: "https://schema.org/ItemListOrderDescending",
            numberOfItems: ARTICLES.length,
            itemListElement: ARTICLES.map((a, i) => ({
              "@type": "ListItem",
              position: i + 1,
              url: `https://arifkhanglobal.com/ask-arif/${a.slug}`,
              name: a.title,
            })),
          },
        ],
      ),
    ],


  }),
});

function InsightsPage() {
  return (
    <PageShell>
      <PageHero
        eyebrow="Insights"
        title="Perspectives on the next decade of building technology."
        description="Articles, guides, learning resources, media and trusted partner references across security, automation, luxury AV and intelligent environments."
      />

      <section className="py-20 border-b border-border">
        <div className="container-page grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {SECTIONS.map((s, i) => (
            <Reveal key={s.to} delay={i * 0.05}>
              <Link to={s.to} className="group relative block h-full rounded-2xl border border-border bg-background p-7 hover:border-border-strong hover:shadow-card transition-all">
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-primary-soft text-primary mb-5">
                  <s.Icon className="h-5 w-5" strokeWidth={1.6} />
                </div>
                <h3 className="text-lg font-semibold tracking-tight">{s.title}</h3>
                <p className="mt-2 text-sm text-ink-soft leading-relaxed">{s.desc}</p>
                <ArrowUpRight className="absolute top-6 right-6 h-4 w-4 text-ink-soft/40 group-hover:text-primary group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-16 border-b border-border bg-surface">
        <div className="container-page flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <span key={c} className="text-xs font-medium px-4 py-2 rounded-full border border-border bg-background text-ink-soft">{c}</span>
          ))}
        </div>
      </section>

      <BehindTheThinking />

      <section className="py-24 border-t border-border">
        <div className="container-page grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {ARTICLES.map((a, i) => (
            <Reveal key={a.slug} delay={i * 0.04}>
              <article className="rounded-2xl border border-border p-8 h-full bg-background hover:border-border-strong transition-colors flex flex-col">
                <span className="eyebrow text-accent">{a.category}</span>
                <h3 className="mt-5 text-xl font-semibold leading-snug tracking-tight">{a.title}</h3>
                <p className="mt-3 text-sm text-ink-soft leading-relaxed flex-1">{a.excerpt}</p>
                <Link to="/ask-arif/$slug" params={{ slug: a.slug }} className="mt-6 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-primary">
                  Read article <ArrowRight className="h-3 w-3" />
                </Link>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-20 border-t border-border bg-surface">
        <div className="container-page max-w-4xl">
          <AuthorCard />
        </div>
      </section>
    </PageShell>
  );
}

function BehindTheThinking() {
  return (
    <section className="py-32 bg-surface border-t border-border overflow-hidden">
      <div className="container-page">
        <div className="max-w-3xl mb-16">
          <span className="eyebrow">The Method</span>
          <h2 className="mt-4 text-balance text-3xl md:text-5xl font-semibold tracking-tight leading-[1.05]">
            Behind the <span className="text-primary">thinking</span>.
          </h2>
          <p className="mt-5 text-pretty text-base md:text-lg text-ink-soft leading-relaxed">
            Ideas, planning and observations gathered from years of experience across Security Infrastructure, Smart Automation and Intelligent Environments.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5 lg:sticky lg:top-28"
          >
            <div className="relative">
              <div aria-hidden className="absolute -inset-6 rounded-[2rem] bg-gradient-to-br from-primary/30 via-primary/10 to-accent/15 blur-2xl opacity-80" />
              <div className="relative overflow-hidden rounded-[1.5rem] glass border border-white/40 shadow-[0_30px_80px_-40px_rgba(21,101,192,0.55)] p-2">
                <div className="relative overflow-hidden rounded-[1.15rem] aspect-[4/5]">
                  <img
                    src={portraitThinking}
                    alt="Arif Khan working — thinking through a project"
                    width={1200}
                    height={1500}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover object-[40%_22%] scale-[1.06]"
                  />
                  <div className="absolute inset-0 bg-[radial-gradient(ellipse_55%_65%_at_38%_42%,transparent_28%,rgba(15,23,42,0.5)_100%)]" />
                  <div className="absolute inset-0 bg-gradient-to-tr from-primary/20 via-transparent to-transparent mix-blend-overlay" />
                  <div aria-hidden className="absolute inset-0 opacity-[0.06] mix-blend-overlay"
                    style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)", backgroundSize: "32px 32px" }}
                  />
                  <div className="absolute inset-x-0 bottom-0 h-1/4 bg-gradient-to-t from-background/85 via-background/30 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-5 flex items-end justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold tracking-tight">Arif Khan</div>
                      <div className="text-xs text-ink-soft mt-0.5">In the work — not adjacent to it</div>
                    </div>
                    <div className="font-mono text-[10px] text-ink-soft tracking-widest">FIELD · 2025</div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {THINKING.map((t, i) => (
              <Reveal key={t.title} delay={i * 0.06}>
                <div className={`group relative h-full rounded-2xl border border-border bg-background p-7 overflow-hidden hover:border-border-strong hover:shadow-card transition-all duration-500 ${i === 0 ? "sm:col-span-2" : ""}`}>
                  <div aria-hidden className="absolute inset-0 bg-gradient-to-br from-primary/8 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                  <div className="relative flex items-start gap-5">
                    <div className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-soft text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-500">
                      <t.Icon className="h-[18px] w-[18px]" strokeWidth={1.6} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold tracking-tight">{t.title}</h3>
                      <p className="mt-2 text-sm text-ink-soft leading-relaxed">{t.body}</p>
                    </div>
                    <ArrowUpRight className="h-4 w-4 text-ink-soft/40 group-hover:text-primary group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300" />
                  </div>
                  <div className="relative mt-6 h-px bg-border overflow-hidden">
                    <div className="absolute inset-y-0 left-0 w-full origin-left scale-x-0 group-hover:scale-x-100 bg-primary transition-transform duration-700 ease-out" />
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
